export const MEGABYTE = 1048576;

/**
 * Returns true if file is larger than the size limit.
 * @param file The file to test.
 * @param sizeLimit Size limit in MB.
 */
export const isFileLargerThan = (file: File, sizeLimit: number): boolean => {
  return file.size > MEGABYTE * sizeLimit;
};

/**
 * Returns true if file is larger than the given byte limit.
 * @param file The file to test.
 * @param sizeLimitBytes Size limit in bytes.
 */
export const isFileLargerThanBytes = (
  file: File,
  sizeLimitBytes: number
): boolean => {
  if (!Number.isFinite(sizeLimitBytes) || sizeLimitBytes <= 0) {
    return false;
  }

  return file.size > sizeLimitBytes;
};

/**
 * Formats a byte count into a human-readable unit (MB/KB/B) for UI copy.
 */
export const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }

  if (bytes >= MEGABYTE) {
    const mb = bytes / MEGABYTE;
    const rounded = Math.round(mb * 10) / 10;

    return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}MB`;
  }

  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)}KB`;
  }

  return `${bytes}B`;
};

/**
 * Returns true if given file is of one of the wanted MIME type, and false otherwise.
 * Will also return false if file is of one of the extension exceptions, even if has the right MIME type.
 * @param file The file to test.
 * @param formats Array of accepted MIME types.
 * @param exceptions Array of extension exceptions.
 */
export const isFileOfType = (
  file: File,
  mimeTypes: string[],
  exceptions: string[] = []
): boolean => {
  return mimeTypes.some(
    (mimeType) =>
      file.type === mimeType && !exceptions.includes(file.name.split('.').pop())
  );
};
